import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CdkDragDrop, CdkDragEnter, CdkDragExit, moveItemInArray } from '@angular/cdk/drag-drop';
import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import { SnAbilityService } from '@lib/ability';
import { SnOperationalView } from '@lib/api/entities';
import { SnOperationalViewsAPI } from '@lib/api/services';
import { SnCurrentUser } from '@lib/auth';
import { t } from '@lib/translation';

import { OperationalViewsService } from '../../services/op-views.service';
import { AddOperationalViewDialogComponent } from '../add-op-view-dialog/add-op-view-dialog.component';

@Component({
  selector: 'sn-op-views-tabs',
  templateUrl: './op-views-tabs.component.html',
  styleUrls: ['./op-views-tabs.component.scss'],
})
export class OperationalViewsTabsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('tabGroup', { static: false }) public childTabGroup!: any;

  @Input() public route: string;

  public views: SnOperationalView[] = [];
  public isLoading: boolean = false;
  public selectedIndex: number;
  public readonly canDelete: boolean;
  public readonly canChange: boolean;
  public readonly canAdd: boolean;

  private readonly _childMenuIds$ = new BehaviorSubject<string[]>([]);

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _operationalViewsAPI: SnOperationalViewsAPI,
    private readonly _ability: SnAbilityService,
    private readonly _operationalViewsService: OperationalViewsService,
    private readonly _router: Router,
    private readonly _dialog: MatDialog,
    private readonly _currentUser: SnCurrentUser,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _toastrService: ToastrService,
  ) {
    this.canDelete = this._ability.can({
      subject: 'dashboards/operationalview',
      action: 'delete',
    });
    this.canChange = this._ability.can({
      subject: 'dashboards/operationalview',
      action: 'change',
    });
    this.canAdd = this._ability.can({
      subject: 'dashboards/operationalview',
      action: 'add',
    });
  }

  get childMenuIds(): string[] {
    return this._childMenuIds$.value;
  }

  get childMenuIds$(): Observable<string[]> {
    return this._childMenuIds$.asObservable();
  }

  public ngOnInit(): void {
    this._fetchViews();
    this._operationalViewsService.viewsChanges$
      .pipe(takeUntil(this._destroy$))
      .subscribe((view) => {
        this._fetchViews();
        this._router.navigate([this.route, view.name, view.id]);
      });
  }

  public ngAfterViewInit(): void {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public trackNavItem(index: number): number {
    return index;
  }

  public onChange(view: SnOperationalView, event: any): void {
    this._operationalViewsAPI
      .updateView(view.id, {
        isPublic: event.checked,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe();
  }

  public deleteView(view: SnOperationalView, index: number): void {
    this._operationalViewsAPI
      .deleteView(view)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.onRemoveMenu(index);
        this._fetchViews(true);
      });
  }

  public addView(): void {
    this._dialog
      .open(AddOperationalViewDialogComponent)
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        if (result.id) {
          this._operationalViewsService.emitView(result);
        }
      });
  }

  public editView(view: SnOperationalView): void {
    this._dialog
      .open(AddOperationalViewDialogComponent, {
        data: view,
      })
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        if (result?.id) {
          this._fetchViews();
        }
      });
  }

  public onDropTab(event: CdkDragDrop<string[]>): void {
    const prevIndex = parseInt(event.previousContainer.id.split('__')[1], 10);
    const newIndex = parseInt(event.container.id.split('__')[1], 10);
    moveItemInArray(this.views, prevIndex, newIndex);
    this._updateConfig();
    this._showDragWrapper(event);
    this._recalculateUniqIdsForDragDrop();
  }

  public onDragEntered(event: CdkDragEnter): void {
    this._hideDragWrapper(event);
  }

  public onDragExited(event: CdkDragExit): void {
    this._showDragWrapper(event);
  }

  public onRemoveMenu(index: number): void {
    this.views.splice(index, 1);
    if (this.childTabGroup.selectedIndex === this.views.length) {
      this.childTabGroup.selectedIndex = this.childTabGroup.selectedIndex - 1;
    }
    this._updateConfig();
    this._recalculateUniqIdsForDragDrop();
  }

  private _showDragWrapper(event: CdkDragExit | CdkDragDrop<string[]>): void {
    const element = this._getDragWrappedElement(event);
    if (element) {
      element.classList.remove('display-none');
    }
  }

  private _hideDragWrapper(event: CdkDragEnter): void {
    const element = this._getDragWrappedElement(event);
    if (element) {
      element.classList.add('display-none');
    }
  }

  private _getDragWrappedElement(event: CdkDragEnter | CdkDragExit): HTMLElement | null {
    return event.container.element.nativeElement.querySelector('.drag-wrapper');
  }

  private _recalculateUniqIdsForDragDrop(): void {
    const uniqIds: string[] = [];
    this.views.forEach((v, i) => uniqIds.push(`${v.id}__${i}`));
    this._childMenuIds$.next(uniqIds);
  }

  private _updateConfig(): void {
    const operationalDashboardConfig = this.views.map((view) => view.id);
    this._currentUser
      .updateSettings({
        dashboardConfiguration: {
          ...this._currentUser.settings.dashboardConfiguration,
          operationalDashboardConfig,
        },
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        error: () => {
          this._toastrService.error(t('dashboard.operational.tabs.unableToSaveConfiguration'));
        },
      });
  }

  private _fetchViews(skipRoute?: boolean): void {
    this.isLoading = true;
    const route = '/dashboard/operational-dashboards';
    this._operationalViewsAPI
      .getViewList({})
      .pipe(takeUntil(this._destroy$))
      .subscribe((response) => {
        const config: string[] =
          this._currentUser.settings.dashboardConfiguration?.operationalDashboardConfig;
        this.views = config?.length
          ? response.results.sort((a, b) => config.indexOf(a.id) - config.indexOf(b.id))
          : response.results;
        if (
          (!this._activatedRoute?.firstChild?.snapshot?.params['id'] && this.views.length) ||
          skipRoute
        ) {
          if (this.views.length) {
            this._router.navigate([route, this.views[0].name, this.views[0].id]);
          } else {
            this._router.navigate([route]);
          }
        }
        this.isLoading = false;
        this._recalculateUniqIdsForDragDrop();
      });
  }
}
